import axios from "axios";

export const API = "https://kodik-api.com"
export const TOKEN = "ada26688e17ff99e4b232191b037be42"
export const ANIME_TYPES = "anime,anime-serial"
export const SEARCH_LIMIT = 100
export const SEARCH_TIMEOUT = 10000
export const MAX_SEARCH_SUPPLEMENT_ROUNDS = 2
const ANIME_SEARCH_TYPE_GROUPS = [ANIME_TYPES, 'anime', 'anime-serial']
let searchRequestSequence = 0
let activeSearchController = null
let titleRequestSequence = 0
let loadingRequestSequence = 0
const releasesByShikimoriCache = new Map()
const releasesByShikimoriInFlight = new Map()

function createAbortError() {
    let error = new Error('Request aborted')
    error.name = 'AbortError'
    error.code = 'ERR_CANCELED'
    return error
}

async function trackLoading(ctx, operation) {
    let token = `request-${++loadingRequestSequence}`
    let scope = `${window.location.pathname}${window.location.search}`
    ctx.commit('BEGIN_LOADING_REQUEST', { token, scope }, { root: true })
    try {
        return await operation()
    } finally {
        ctx.commit('END_LOADING_REQUEST', token, { root: true })
    }
}

function normalizeIdentityPart(value) {
    return String(value || '')
        .normalize('NFKC')
        .trim()
        .toLowerCase()
        .replace(/ё/g, 'е')
        .replace(/\s+/g, ' ')
}

function normalizeSearchText(value) {
    return normalizeIdentityPart(value)
        .replace(/[^\p{L}\p{N}]+/gu, ' ')
        .trim()
}

function toSearchNames(value) {
    let values = Array.isArray(value) ? value : [value]
    return values
        .filter(Boolean)
        .flatMap((name) => [name, ...String(name).split(/[\/|]/)])
        .map(normalizeSearchText)
        .filter(Boolean)
}

function stripAnimeFormatSuffix(title) {
    return title
        .replace(/\s+(?:тв|tv|сезон|season)(?:\s+\d+)?(?:\s+часть\s+\d+)?$/u, '')
        .replace(/\s+(?:ova|она|ona|спецвыпуск|special)(?:\s+\d+)?$/u, '')
        .trim()
}

function displayedSeasonIdentity(item) {
    let title = normalizeSearchText(item && item['title'])
    let match = title.match(/(?:тв|tv|сезон|season)\s*-?\s*(\d{1,2})(?:\s|$)/u)
    let season = match && Number(match[1])
    let branch = stripAnimeFormatSuffix(title)
    return season && season <= 99 && branch ? { branch, season } : null
}

function editDistance(first, second) {
    let previousRow = Array.from({ length: second.length + 1 }, (_, index) => index)
    for (let firstIndex = 1; firstIndex <= first.length; firstIndex++) {
        let currentRow = [firstIndex]
        for (let secondIndex = 1; secondIndex <= second.length; secondIndex++) {
            let substitutionCost = first[firstIndex - 1] === second[secondIndex - 1] ? 0 : 1
            currentRow[secondIndex] = Math.min(
                currentRow[secondIndex - 1] + 1,
                previousRow[secondIndex] + 1,
                previousRow[secondIndex - 1] + substitutionCost
            )
        }
        previousRow = currentRow
    }
    return previousRow[second.length]
}

function approximatelyMatchesName(name, normalizedQuery) {
    if (normalizedQuery.length < 4 || normalizedQuery.length > 80) return false

    let queryTokens = normalizedQuery.split(' ')
    let nameTokens = new Set(name.split(' '))
    if (queryTokens.length > 1) {
        let meaningfulQueryTokens = queryTokens.filter((token) => {
            return token.length >= 3 && !SEARCH_STOP_TOKENS.has(token)
        })
        if (meaningfulQueryTokens.length < 2) return false
        if (meaningfulQueryTokens.every((token) => nameTokens.has(token))) return true

        let comparableName = stripAnimeFormatSuffix(name)
        if (Math.abs(comparableName.length - normalizedQuery.length) > 4) return false
        let maxDistance = Math.min(4, Math.max(2, Math.floor(normalizedQuery.length * 0.18)))
        return editDistance(comparableName, normalizedQuery) <= maxDistance
    }

    let comparableName = name.slice(0, normalizedQuery.length)
    let maxDistance = normalizedQuery.length <= 5
        ? 1
        : Math.min(4, Math.max(2, Math.floor(normalizedQuery.length * 0.18)))
    return editDistance(comparableName, normalizedQuery) <= maxDistance
}

function matchesDisplayedFranchise(name, normalizedQuery) {
    let strippedName = stripAnimeFormatSuffix(name)
    if (strippedName === normalizedQuery) return true
    if (strippedName.length >= 4 && normalizedQuery.includes(strippedName)) return true
    return Math.abs(strippedName.length - normalizedQuery.length) <= 4
        && approximatelyMatchesName(strippedName, normalizedQuery)
}

function queryTokensMatchName(name, normalizedQuery) {
    /* Двухбуквенные romaji-частицы (no/wa/ni/ga) слишком часты и создают
       сотни ложных совпадений вроде запроса Kimi no Na wa. */
    let rawQueryTokens = normalizedQuery.split(' ')
    let queryTokens = rawQueryTokens
        .filter((token) => token.length >= 3 && !SEARCH_STOP_TOKENS.has(token))
    let nameTokens = new Set(name.split(' '))
    if (!queryTokens.length) return false

    let matchingTokens = queryTokens.filter((token) => nameTokens.has(token)).length
    let requiredMatches = rawQueryTokens.length === 1 ? 1 : 2
    return matchingTokens >= requiredMatches
}

const SEARCH_STOP_TOKENS = new Set([
    'anime', 'watch', 'online', 'movie', 'film', 'serial', 'season',
    'аниме', 'смотреть', 'онлайн', 'фильм', 'сериал', 'сезон',
    'the', 'and', 'with', 'from',
])

function meaningfulSearchTokens(value) {
    return normalizeSearchText(value)
        .split(' ')
        .filter((token) => token.length >= 3 && !SEARCH_STOP_TOKENS.has(token))
}

function itemSearchNames(item) {
    let material = item['material_data'] || {}
    return toSearchNames([
        item['title'],
        item['title_orig'],
        item['other_title'],
        material['anime_title'],
        material['title'],
        material['title_en'],
        material['anime_license_name'],
        material['other_titles'],
        material['other_titles_en'],
        material['other_titles_jp'],
    ].flat())
}

function searchRelevance(item, normalizedQuery) {
    let material = item['material_data'] || {}
    let primaryNames = toSearchNames([
        item['title'],
        item['title_orig'],
        material['anime_title'],
        material['anime_license_name'],
    ])
    let alternativeNames = toSearchNames([
        item['other_title'],
        material['other_titles'],
        material['other_titles_en'],
        material['other_titles_jp'],
    ].flat())
    let genericNames = toSearchNames([material['title'], material['title_en']])
    let allNames = [...primaryNames, ...alternativeNames, ...genericNames]

    if (primaryNames.includes(normalizedQuery)) return 0

    let strippedPrimaryMatch = primaryNames.some((name) => {
        return stripAnimeFormatSuffix(name) === normalizedQuery
    })
    if (strippedPrimaryMatch) {
        let animeKind = material['anime_kind']
        let isTv = animeKind ? animeKind === 'tv' : item['type'] === 'anime-serial'
        return isTv ? 1 : 3
    }

    if ([...alternativeNames, ...genericNames].includes(normalizedQuery)) return 2
    if (primaryNames.some((name) => name.startsWith(`${normalizedQuery} `))) return 3
    if (allNames.some((name) => name.startsWith(`${normalizedQuery} `))) return 4
    if (allNames.some((name) => name.includes(normalizedQuery))) return 5
    if (allNames.some((name) => {
        let strippedName = stripAnimeFormatSuffix(name)
        return strippedName.length >= 4 && normalizedQuery.includes(strippedName)
    })) return 5
    if (allNames.some((name) => queryTokensMatchName(name, normalizedQuery))) return 5
    if (allNames.some((name) => approximatelyMatchesName(name, normalizedQuery))) return 5
    return 6
}

function searchFormatPreference(item, normalizedQuery) {
    let material = item['material_data'] || {}
    let isTv = material['anime_kind']
        ? material['anime_kind'] === 'tv'
        : item['type'] === 'anime-serial'
    if (!isTv) return 100

    let displayedNames = toSearchNames([item['title'], item['title_orig']])
    let displayedFranchiseMatch = displayedNames.some((name) => {
        return matchesDisplayedFranchise(name, normalizedQuery)
    })
    if (!displayedFranchiseMatch) return 100

    /* Если Kodik дал одно franchise-name нескольким сезонам, сначала
       показываем базовый сезон, затем продолжения. */
    let seasonNumbers = displayedNames
        .map((name) => name.match(/(?:тв|tv|сезон)\s*-?\s*(\d+)/u))
        .filter(Boolean)
        .map((match) => Number(match[1]))
    return seasonNumbers.length ? Math.min(...seasonNumbers) : 0
}

/* Kodik хорошо находит варианты названий, но внутри франшизы может поставить
   фильм или OVA выше основного ТВ-сериала из-за приоритета свежей озвучки.
   Поднимаем точные совпадения, сохраняя исходный порядок внутри одного уровня. */
export function rankSearchResults(results, title) {
    let normalizedQuery = normalizeSearchText(title)
    if (!normalizedQuery) return results || []

    let scoredResults = (results || [])
        .map((item, index) => ({
            item,
            index,
            relevance: searchRelevance(item, normalizedQuery),
            formatPreference: searchFormatPreference(item, normalizedQuery),
        }))

    let confirmedShikimoriIds = new Set(
        scoredResults
            .filter(({ relevance }) => relevance < 6)
            .map(({ item }) => normalizeIdentityPart(item['shikimori_id']))
            .filter(Boolean)
    )

    let confirmedCoreTokens = new Set()
    let queryCoreTokens = new Set(meaningfulSearchTokens(normalizedQuery))
    let confirmedQueryAnchors = new Set()
    for (const { item, relevance } of scoredResults) {
        if (relevance >= 6) continue
        for (const name of itemSearchNames(item)) {
            let strippedName = stripAnimeFormatSuffix(name)
            if (strippedName.length >= 4 && (
                normalizedQuery.includes(strippedName)
                || approximatelyMatchesName(strippedName, normalizedQuery)
            )) {
                confirmedQueryAnchors.add(strippedName)
            }
            for (const token of meaningfulSearchTokens(name)) {
                if (queryCoreTokens.has(token)) {
                    confirmedCoreTokens.add(token)
                }
            }
        }
    }

    /* Не показываем fuzzy-хвост Kodik, в котором запрос не подтверждается
       ни одним доступным названием. Небольшие опечатки и перестановки слов
       уже распознаны searchRelevance и остаются в выдаче. */
    let singleTokenAnchor = confirmedQueryAnchors.size === 1
        && [...confirmedQueryAnchors][0].split(' ').length === 1
    scoredResults = scoredResults.filter(({ item, relevance }) => {
        if (relevance < 6) return true

        let shikimoriId = normalizeIdentityPart(item['shikimori_id'])
        if (shikimoriId && confirmedShikimoriIds.has(shikimoriId)) return true

        return confirmedQueryAnchors.size > 0 && itemSearchNames(item).some((name) => {
            if ([...confirmedQueryAnchors].some((anchor) => name.includes(anchor))) return true
            return singleTokenAnchor
                && meaningfulSearchTokens(name).some((token) => confirmedCoreTokens.has(token))
        })
    })

    let orderedResults = scoredResults.sort((first, second) => {
            return first.relevance - second.relevance
                || first.formatPreference - second.formatPreference
                || first.index - second.index
        })

    /* Внутри уже равного relevance-bucket сохраняем позиции франшизы, но
       расставляем явно подписанные сезоны одной ветки по возрастанию. */
    let seasonGroups = new Map()
    orderedResults.forEach((entry, position) => {
        let identity = displayedSeasonIdentity(entry.item)
        if (!identity) return
        let key = `${entry.relevance}:${entry.formatPreference}:${identity.branch}`
        if (!seasonGroups.has(key)) seasonGroups.set(key, [])
        seasonGroups.get(key).push({ entry, position, season: identity.season })
    })
    for (const group of seasonGroups.values()) {
        if (group.length < 2) continue
        let positions = group.map(({ position }) => position)
        let sortedEntries = [...group]
            .sort((first, second) => first.season - second.season || first.entry.index - second.entry.index)
            .map(({ entry }) => entry)
        positions.forEach((position, index) => { orderedResults[position] = sortedEntries[index] })
    }

    return orderedResults.map(({ item }) => item)
}

/* В Kodik внешний ID не всегда обозначает одно произведение: например,
   разные сезоны могут иметь общий kinopoisk_id, а разные аниме-фильмы —
   общий shikimori_id/worldart_link. Поэтому одного совпавшего ID недостаточно.
   Озвучки считаем дублями только при совпадении самого произведения. */
export function isSameAnimeWork(first, second) {
    if (!first || !second) return false

    let firstType = normalizeIdentityPart(first['type'])
    let secondType = normalizeIdentityPart(second['type'])
    let typesConflict = firstType && secondType && firstType !== secondType

    /* Иногда одно произведение заведено в Kodik под разными локализованными
       названиями. Объединяем такие алиасы только при трёх совпавших внешних ID
       и полном отсутствии конфликтов между известными ID. Двух совпадений
       недостаточно: у самостоятельных Death Note Rewrite совпадают Shikimori
       и WorldArt, а Kinopoisk уже разный. */
    let identityFields = ['shikimori_id', 'kinopoisk_id', 'imdb_id', 'worldart_link']
    let matchingIdentityFields = 0
    let conflictingIdentityFields = 0
    for (const field of identityFields) {
        let firstValue = normalizeIdentityPart(first[field])
        let secondValue = normalizeIdentityPart(second[field])
        if (!firstValue || !secondValue) continue
        if (firstValue === secondValue) matchingIdentityFields++
        else conflictingIdentityFields++
    }
    if (!conflictingIdentityFields && matchingIdentityFields >= 3) return true
    if (typesConflict || conflictingIdentityFields) return false

    let firstShikimoriId = normalizeIdentityPart(first['shikimori_id'])
    let secondShikimoriId = normalizeIdentityPart(second['shikimori_id'])

    let firstTitle = normalizeIdentityPart(first['title'])
    let secondTitle = normalizeIdentityPart(second['title'])
    let firstOriginalTitle = normalizeIdentityPart(first['title_orig'])
    let secondOriginalTitle = normalizeIdentityPart(second['title_orig'])

    let titleMatches = Boolean(firstTitle && secondTitle && firstTitle === secondTitle)
    let originalTitleMatches = Boolean(
        firstOriginalTitle && secondOriginalTitle && firstOriginalTitle === secondOriginalTitle
    )
    if (firstTitle && secondTitle && !titleMatches) return false
    if (firstOriginalTitle && secondOriginalTitle && !originalTitleMatches) return false

    /* Для одного Shikimori ID совпадение любого основного названия надёжнее
       года: у разных озвучек Kodik год иногда расходится. Сам ID без названия
       недостаточен — база переиспользует его и для самостоятельных фильмов. */
    if (firstShikimoriId && secondShikimoriId) {
        return titleMatches || originalTitleMatches
    }

    let firstYear = normalizeIdentityPart(first['year'])
    let secondYear = normalizeIdentityPart(second['year'])
    if (firstYear && secondYear && firstYear !== secondYear) return false
    if (titleMatches || originalTitleMatches) return true

    let firstOtherTitle = normalizeIdentityPart(first['other_title'])
    let secondOtherTitle = normalizeIdentityPart(second['other_title'])
    if (firstOtherTitle && secondOtherTitle) return firstOtherTitle === secondOtherTitle

    /* Редкий fallback для записей без названий. Kodik ID здесь полезен,
       потому что иначе такие неполные записи невозможно безопасно объединить. */
    return Boolean(first['id'] && second['id'] && first['id'] === second['id'])
}

/* Kodik возвращает отдельную запись на каждую озвучку. Сохраняем первую —
   API ставит наиболее подходящую и полную озвучку выше остальных. */
export function dedupe(results) {
    let uniqueResults = []
    for (const item of results || []) {
        if (!item || uniqueResults.some((savedItem) => isSameAnimeWork(savedItem, item))) continue
        uniqueResults.push(item)
    }
    return uniqueResults
}

function responseResults(response) {
    return Array.isArray(response && response.data && response.data.results)
        ? response.data.results
        : []
}

function requestSearchWindow(title, types, signal, blockedTranslationIds = []) {
    let params = {
        token: TOKEN,
        types,
        title,
        limit: SEARCH_LIMIT,
        with_material_data: true,
    }
    if (blockedTranslationIds.length) {
        params.block_translations = blockedTranslationIds.join(',')
    }
    return axios.get(`${API}/search`, {
        params,
        timeout: SEARCH_TIMEOUT,
        signal,
    })
}

/* Связанные релизы разрешаем в Kodik только по клику. Это не создаёт N
   запросов при открытии большой франшизы, а повторные клики используют
   memory cache и один общий in-flight запрос. */
export async function searchAnimeByShikimoriId(shikimoriId, { signal } = {}) {
    let normalizedId = String(shikimoriId || '').trim()
    if (!/^\d+$/.test(normalizedId) || normalizedId === '0') return []
    if (signal && signal.aborted) return Promise.reject(createAbortError())
    if (releasesByShikimoriCache.has(normalizedId)) {
        return releasesByShikimoriCache.get(normalizedId)
    }
    let entry = releasesByShikimoriInFlight.get(normalizedId)
    if (!entry) {
        let controller = typeof AbortController === 'undefined' ? null : new AbortController()
        entry = { controller, subscribers: 0, settled: false, promise: null }
        entry.promise = axios.get(`${API}/search`, {
            params: {
                token: TOKEN,
                types: ANIME_TYPES,
                shikimori_id: normalizedId,
                limit: SEARCH_LIMIT,
                with_material_data: true,
            },
            timeout: SEARCH_TIMEOUT,
            signal: controller ? controller.signal : undefined,
        })
            .then((response) => dedupe(responseResults(response)))
            .then((results) => {
                releasesByShikimoriCache.set(normalizedId, results)
                return results
            })
            .then((results) => {
                entry.settled = true
                if (releasesByShikimoriInFlight.get(normalizedId) === entry) {
                    releasesByShikimoriInFlight.delete(normalizedId)
                }
                return results
            }, (error) => {
                entry.settled = true
                if (releasesByShikimoriInFlight.get(normalizedId) === entry) {
                    releasesByShikimoriInFlight.delete(normalizedId)
                }
                throw error
            })
        releasesByShikimoriInFlight.set(normalizedId, entry)
    }

    entry.subscribers++
    return new Promise((resolve, reject) => {
        let finished = false
        let onAbort
        let finish = () => {
            if (finished) return false
            finished = true
            if (signal && onAbort) signal.removeEventListener('abort', onAbort)
            entry.subscribers--
            if (!entry.settled && entry.subscribers === 0) {
                if (releasesByShikimoriInFlight.get(normalizedId) === entry) {
                    releasesByShikimoriInFlight.delete(normalizedId)
                }
                if (entry.controller) entry.controller.abort()
            }
            return true
        }
        onAbort = () => {
            if (!finish()) return
            reject(createAbortError())
        }
        if (signal) signal.addEventListener('abort', onAbort, { once: true })
        entry.promise.then((results) => {
            if (!finish()) return
            resolve(results)
        }, (error) => {
            if (!finish()) return
            reject(error)
        })
    })
}

export function clearShikimoriReleaseCache() {
    releasesByShikimoriCache.clear()
    for (const entry of releasesByShikimoriInFlight.values()) {
        if (entry.controller) entry.controller.abort()
    }
    releasesByShikimoriInFlight.clear()
}

async function requestSupplementWindows(title, types, initialResults, signal) {
    let resultGroups = []
    let currentResults = initialResults
    let blockedTranslationIds = new Set()

    for (let round = 0; round < MAX_SEARCH_SUPPLEMENT_ROUNDS; round++) {
        if (currentResults.length < SEARCH_LIMIT) break

        let blockedCount = blockedTranslationIds.size
        for (const item of currentResults) {
            let translationId = item && item.translation && item.translation.id
            if (translationId !== undefined && translationId !== null) {
                blockedTranslationIds.add(String(translationId))
            }
        }
        if (blockedTranslationIds.size === blockedCount) break

        let response = await requestSearchWindow(
            title,
            types,
            signal,
            [...blockedTranslationIds]
        )
        currentResults = responseResults(response)
        if (!currentResults.length) break
        resultGroups.push(currentResults)
    }
    return resultGroups
}

/* Общий запрос сохраняет глобальный порядок релевантности, а два отдельных
   запроса не дают фильмам и сериалам вытеснить друг друга из лимита /search.
   У /search нет пагинации, поэтому берём максимальные 100 записей в каждом
   доступном окне и затем схлопываем только озвучки одного произведения. */
export async function searchAnime(title, { signal, compact = false } = {}) {
    let normalizedTitle = String(title || '').trim()
    if (!normalizedTitle) return []

    if (compact) {
        let response = await requestSearchWindow(normalizedTitle, ANIME_TYPES, signal)
        return rankSearchResults(dedupe(responseResults(response)), normalizedTitle).slice(0, 20)
    }

    let requests = ANIME_SEARCH_TYPE_GROUPS.map((types) => {
        return requestSearchWindow(normalizedTitle, types, signal)
    })
    let [combinedResponse, movieResponse, serialResponse] = await Promise.allSettled(requests)

    /* Окна отдельных типов обеспечивают охват и обязательны. Общее окно
       используется для порядка релевантности; при его сбое полнота двух
       типовых окон сохраняется, поэтому можно безопасно продолжить. */
    if (movieResponse.status === 'rejected') throw movieResponse.reason
    if (serialResponse.status === 'rejected') throw serialResponse.reason

    let combinedResults = combinedResponse.status === 'fulfilled'
        ? responseResults(combinedResponse.value)
        : []
    let movieResults = responseResults(movieResponse.value)
    let serialResults = responseResults(serialResponse.value)
    let [movieSupplements, serialSupplements] = await Promise.all([
        requestSupplementWindows(normalizedTitle, 'anime', movieResults, signal),
        requestSupplementWindows(normalizedTitle, 'anime-serial', serialResults, signal),
    ])

    let resultGroups = [
        combinedResults,
        movieResults,
        serialResults,
        ...movieSupplements,
        ...serialSupplements,
    ]
    return rankSearchResults(dedupe(resultGroups.flat()), normalizedTitle)
}

export async function fetchKodikTitle(id, { signal } = {}) {
    let normalizedId = String(id || '').trim()
    if (!normalizedId) return null
    let response = await axios.get(`${API}/search`, {
        params: {
            token: TOKEN,
            id: normalizedId,
            with_material_data: true,
            with_episodes_data: true,
        },
        timeout: SEARCH_TIMEOUT,
        signal,
    })
    return responseResults(response)[0] || null
}

export default {
    state: {
        kodik_genres: {},
        kodik_genres_next: {},
        kodik_search_results: [],
        kodik_search_error: '',
        kodik_search_status: 'idle',
        kodik_title: {},
        kodik_schedule: [],
    },
    getters: {
        get_kodik_genres: (state) => state.kodik_genres,
        get_kodik_genres_next: (state) => state.kodik_genres_next,
        get_kodik_search_results: (state) => state.kodik_search_results,
        get_kodik_search_error: (state) => state.kodik_search_error,
        get_kodik_search_status: (state) => state.kodik_search_status,
        get_kodik_title: (state) => state.kodik_title,
        get_kodik_schedule: (state) => state.kodik_schedule,
    },
    actions: {
        async request_kodik_genres(ctx, data) {
            /* data = [жанр, append]; append=true — следующая страница жанра.
               Запрашиваем всегда 100 (максимум): каждая озвучка — отдельная запись,
               и после дедупликации уникальных тайтлов остаётся в разы меньше.
               Отдаём всю дедуплицированную страницу — срез терял бы тайтлы,
               потому что next_page продолжает после всех 100 сырых записей */
            let genre = data[0], append = data[1]
            let url = `${API}/list?token=${TOKEN}&types=${ANIME_TYPES}&anime_genres=${encodeURIComponent(genre)}&sort=shikimori_rating&limit=100&with_material_data=true`
            if (append) {
                url = ctx.state.kodik_genres_next[genre]
                if (!url) return
            }
            return trackLoading(ctx, async () => {
                let response = await axios.get(url)
                ctx.commit("SET_KODIK_GENRES", [genre, dedupe(response['data']['results'])])
                ctx.commit("SET_KODIK_GENRES_NEXT", [genre, response['data']['next_page']])
            })
        },
        async request_kodik_results(ctx, title) {
            /* Живой поиск по всем аниме-фильмам и аниме-сериалам */
            let requestSequence = ++searchRequestSequence
            if (activeSearchController) activeSearchController.abort()
            activeSearchController = null
            let normalizedTitle = String(title || '').trim()
            if (!normalizedTitle) {
                ctx.commit("SET_KODIK_SEARCH_RESULTS", [])
                ctx.commit("SET_KODIK_SEARCH_ERROR", '')
                ctx.commit("SET_KODIK_SEARCH_STATUS", 'idle')
                return
            }
            let searchController = typeof AbortController === 'undefined' ? null : new AbortController()
            activeSearchController = searchController
            ctx.commit("SET_KODIK_SEARCH_RESULTS", [])
            ctx.commit("SET_KODIK_SEARCH_ERROR", '')
            ctx.commit("SET_KODIK_SEARCH_STATUS", 'loading')
            return trackLoading(ctx, async () => {
                try {
                    let results = await searchAnime(normalizedTitle, {
                        signal: searchController ? searchController.signal : undefined,
                    })
                    if (requestSequence === searchRequestSequence) {
                        ctx.commit("SET_KODIK_SEARCH_RESULTS", results)
                        ctx.commit("SET_KODIK_SEARCH_STATUS", 'success')
                    }
                } catch (error) {
                    if (requestSequence === searchRequestSequence) {
                        ctx.commit("SET_KODIK_SEARCH_RESULTS", [])
                        ctx.commit("SET_KODIK_SEARCH_ERROR", 'Не удалось загрузить результаты. Попробуйте ещё раз.')
                        ctx.commit("SET_KODIK_SEARCH_STATUS", 'error')
                    }
                } finally {
                    if (activeSearchController === searchController) activeSearchController = null
                }
            })
        },
        invalidate_kodik_search(ctx) {
            searchRequestSequence++
            if (activeSearchController) activeSearchController.abort()
            activeSearchController = null
            ctx.commit("SET_KODIK_SEARCH_RESULTS", [])
            ctx.commit("SET_KODIK_SEARCH_ERROR", '')
            ctx.commit("SET_KODIK_SEARCH_STATUS", 'idle')
        },
        async request_kodik_title(ctx, id) {
            /* Один тайтл по Kodik ID (serial-123 / movie-123).
               Сбрасываем предыдущий тайтл, чтобы при переходе
               не мигал плеер/постер прошлой страницы */
            let requestSequence = ++titleRequestSequence
            ctx.commit("SET_KODIK_TITLE", {})
            return trackLoading(ctx, async () => {
                let title = await fetchKodikTitle(id)
                if (requestSequence === titleRequestSequence) {
                    ctx.commit("SET_KODIK_TITLE", title || {})
                }
            })
        },
        async request_kodik_schedule(ctx) {
            /* Расписание: онгоинги, сгруппированные по дню недели next_episode_at */
            return trackLoading(ctx, async () => {
                let response = await axios.get(`${API}/list?token=${TOKEN}&types=anime-serial&anime_status=ongoing&sort=updated_at&limit=100&with_material_data=true`)
                let days = [[], [], [], [], [], [], []]
                for (const item of dedupe(response['data']['results'])) {
                    let date = (item['material_data'] || {})['next_episode_at'] || item['updated_at']
                    let day = (new Date(date).getDay() + 6) % 7 /* 0 = понедельник */
                    days[day].push(item)
                }
                ctx.commit("SET_KODIK_SCHEDULE", days.map((list, day) => ({ day, list })))
            })
        },
    },
    mutations: {
        SET_KODIK_GENRES(state, args) {
            state.kodik_genres[args[0]] = args[1]
        },
        SET_KODIK_GENRES_NEXT(state, args) {
            state.kodik_genres_next[args[0]] = args[1]
        },
        SET_KODIK_SEARCH_RESULTS(state, data) {
            state.kodik_search_results = data
        },
        SET_KODIK_SEARCH_ERROR(state, data) {
            state.kodik_search_error = data
        },
        SET_KODIK_SEARCH_STATUS(state, data) {
            state.kodik_search_status = data
        },
        SET_KODIK_TITLE(state, data) {
            state.kodik_title = data
        },
        SET_KODIK_SCHEDULE(state, data) {
            state.kodik_schedule = data
        },
    }
}
